import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArticleIcon from "@mui/icons-material/Article";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import {
  FaCloud,
  FaCode,
  FaCogs,
  FaFileContract,
  FaProjectDiagram,
  FaRobot,
  FaShieldAlt,
} from "react-icons/fa";
import { MdSupportAgent, MdTransform } from "react-icons/md";
import React from "react";
import { useNavigate } from "react-router-dom";
import { designTokens } from "../../../theme/designTokens";
import { whatWeDoCards } from "../../../theme/homeContent";
import SectionHeading from "../shared/SectionHeading";

const { colors } = designTokens;

const cardTopIcons = [
  <ArticleIcon key="clm" sx={{ fontSize: 28 }} />,
  <AutoAwesomeIcon key="ai" sx={{ fontSize: 28 }} />,
  <BusinessCenterIcon key="consulting" sx={{ fontSize: 28 }} />,
];

const cardItemIcons = [
  [FaFileContract, FaShieldAlt, FaProjectDiagram],
  [FaRobot, MdSupportAgent, FaCogs],
  [MdTransform, FaCode, FaCloud],
] as const;

const InnovativeSolutions = () => {
  const navigate = useNavigate();

  const handleClick = (card: (typeof whatWeDoCards)[number]) => {
    if (card.external) {
      window.open(card.path, "_blank");
    } else {
      navigate(card.path);
    }
  };

  return (
    <Box id="what-we-do" sx={{ py: designTokens.spacing.sectionPy, backgroundColor: colors.background }}>
      <Container maxWidth={designTokens.spacing.containerMax}>
        <SectionHeading
          label="WHAT WE DO"
          title="Innovative Solutions"
          highlight="Driving Growth & Impact."
          subtitle="We combine expertise and innovation to deliver solutions that help our clients scale , transform and succeed"
        />
        <Grid container spacing={3} justifyContent="center">
          {whatWeDoCards.map((card, cardIndex) => (
            <Grid item xs={12} sm={6} md={4} key={card.title}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: `1px solid ${colors.primary}`,
                  borderRadius: "10px",
                  maxWidth: 320,
                  mx: "auto",
                  boxShadow:
                    "0px 4px 8px 3px rgba(254, 121, 21, 0.3), 0px 1px 3px 0px rgba(0, 0, 0, 0.3)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 54,
                      height: 50,
                      borderRadius: "50%",
                      backgroundColor: colors.primary,
                      color: colors.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    {cardTopIcons[cardIndex]}
                  </Box>

                  <Box sx={{ display: "inline-block", mb: 2.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                      {card.title}
                    </Typography>
                    <Box
                      sx={{
                        width: 43,
                        height: "2.5px",
                        backgroundColor: colors.primary,
                        mx: "auto",
                        mt: 1,
                        borderRadius: 1,
                      }}
                    />
                  </Box>

                  <Box sx={{ textAlign: "left", mb: 2 }}>
                    {card.items.map((item, itemIndex) => {
                      const ItemIcon = cardItemIcons[cardIndex][itemIndex] as React.FC<{ size?: number }>;
                      return (
                        <Box key={item}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              py: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                width: 25,
                                height: 25,
                                minWidth: 25,
                                backgroundColor: colors.primary,
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: colors.white,
                              }}
                            >
                              <ItemIcon size={14} />
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ color: colors.textPrimary, fontWeight: 500, fontSize: "0.875rem" }}
                            >
                              {item}
                            </Typography>
                          </Box>
                          {itemIndex < card.items.length - 1 && (
                            <Divider sx={{ borderColor: colors.border }} />
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleClick(card)}
                    sx={{ px: 3, py: 1, letterSpacing: 0.5 }}
                  >
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default InnovativeSolutions;
